import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../utils/ApiManager';
import Navbar from '../components/Navbar';
import { theme } from '../utils/theme';

const Registration = () => {
  const { t, i18n } = useTranslation('registration');
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    acceptTerms: false,
    newsletterConsent: false
  });
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormData({
      ...formData,
      [e.target.name]: value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.acceptTerms) {
      setError(t('errors.acceptTerms'));
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError(t('errors.passwordMatch'));
      return;
    }

    try {
      await api.post('/api/auth/register', {
        name: formData.name,
        email: formData.email,
        password: formData.password,
        defaultLanguage: i18n.language || 'en', // Use current language from i18n
        newsletterConsent: formData.newsletterConsent
      });

      // Navigate to home page with success message
      navigate('/', { 
        state: { 
          message: t('verificationEmailSent'),
          type: 'success'
        } 
      });
    } catch (err) {
      console.log('Registration error:', err.response?.data);  // Add this line for debugging
      setError(err.response?.data?.error || t('errors.failed'));
    }
  };

  return (
    <div className="min-h-screen bg-background-primary">
      <Navbar />
      <div className="pt-24 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md mx-auto bg-[#0C3B2E] text-white rounded-lg shadow-lg overflow-hidden">
          <div className="px-6 py-8">
            <div className="text-center">
              <h2 className="text-3xl font-bold">
                {t('title')}
              </h2>
              <p className="mt-2 text-sm text-white/80">
                {t('haveAccount')}{' '}
                <Link to="/login" className="font-medium text-[#FFBA00] hover:text-white">
                  {t('signIn')}
                </Link>
              </p>
            </div>

            <form className="mt-8 space-y-6" onSubmit={handleSubmit} autoComplete="off">
              {error && (
                <div className="bg-red-900/50 border-l-4 border-red-400 p-4 rounded" role="alert">
                  <p className="text-sm text-white">{error}</p>
                </div>
              )}

              <div className="space-y-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium">
                    {t('labels.name')}
                  </label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    required
                    autoComplete="off"
                    className="mt-1 block w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white shadow-sm focus:outline-none focus:ring-2 focus:ring-[#6D9773] focus:border-transparent"
                    value={formData.name}
                    onChange={handleChange}
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium">
                    {t('labels.email')}
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    autoComplete="off"
                    className="mt-1 block w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white shadow-sm focus:outline-none focus:ring-2 focus:ring-[#6D9773] focus:border-transparent"
                    value={formData.email}
                    onChange={handleChange}
                  />
                </div>

                <div>
                  <label htmlFor="password" className="block text-sm font-medium">
                    {t('labels.password')}
                  </label>
                  <input
                    id="password"
                    name="password"
                    type="password"
                    required
                    autoComplete="new-password"
                    className="mt-1 block w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white shadow-sm focus:outline-none focus:ring-2 focus:ring-[#6D9773] focus:border-transparent"
                    value={formData.password}
                    onChange={handleChange}
                  />
                </div>

                <div>
                  <label htmlFor="confirmPassword" className="block text-sm font-medium">
                    {t('labels.confirmPassword')}
                  </label>
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type="password"
                    required
                    autoComplete="new-password"
                    className="mt-1 block w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white shadow-sm focus:outline-none focus:ring-2 focus:ring-[#6D9773] focus:border-transparent"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                  />
                </div>

                <div className="flex items-start">
                  <div className="flex items-center h-5">
                    <input
                      id="newsletterConsent"
                      name="newsletterConsent"
                      type="checkbox"
                      checked={formData.newsletterConsent}
                      onChange={handleChange}
                      className="w-4 h-4 bg-white/10 border-white/20 rounded focus:ring-2 focus:ring-[#6D9773]"
                    />
                  </div>
                  <div className="ml-3">
                    <label htmlFor="newsletterConsent" className="text-sm text-white/80">
                      {t('labels.newsletterConsent')}
                    </label>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="flex items-center h-5">
                    <input
                      id="acceptTerms"
                      name="acceptTerms"
                      type="checkbox"
                      checked={formData.acceptTerms}
                      onChange={handleChange}
                      className="w-4 h-4 bg-white/10 border-white/20 rounded focus:ring-2 focus:ring-[#6D9773]"
                    />
                  </div>
                  <div className="ml-3">
                    <label htmlFor="acceptTerms" className="text-sm text-white/80">
                      {t('agreeToTerms')}{' '}
                      <Link to="/terms" className="font-medium text-[#FFBA00] hover:text-white">
                        {t('termsAndConditions')}
                      </Link>
                    </label>
                  </div>
                </div>
              </div>

              <div>
                <button
                  type="submit"
                  className={theme.button.base + ' ' + theme.button.cta + ' w-full ' + theme.button.sizes.lg}
                >
                  {t('createAccount')}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Registration;